import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

import LoginModel from '../../../../../models/user.model';
import { AppCommon } from '../../../../../providers/app-common';
import { SourcingNeedV2ViewModel } from '../../../../../models-views/sourcing-needs-v2.view.model';
import { StandardResponseModel } from '../../../../../models/project.model';

@Component({
  selector: 'app-sn-form-venue-v2',
  templateUrl: './sn-form-venue-v2.component.html',
  styleUrls: ['./sn-form-venue-v2.component.scss'],
  standalone: false
})
export class SnFormVenueV2Component implements OnInit, OnChanges {
  @Input() eventId: number | null = null;
  @Input() sourcingNeedRecordId: number | null = null;
  @Input() data: any;
  @Output() closeModal = new EventEmitter<'success' | 'cancel'>();

  form!: FormGroup;
  isEdit: boolean = true;
  isView: boolean = false;
  existingData: any = null;


  venueOptionsResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  roomTypesResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  seatingStylesResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());

  constructor(
    private fb: FormBuilder,
    private vm: SourcingNeedV2ViewModel,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    console.log('[SnFormVenueV2] initial @Input values from parent:', {
      eventId: this.eventId,
      sourcingNeedRecordId: this.sourcingNeedRecordId,
      data: this.data,
    });

    this.vm.parentSnFormVenueV2Component = this;
    this.initForm();
    this.vm.subscribeToVenue(
      this.venueOptionsResponseModel,
      this.roomTypesResponseModel,
      this.seatingStylesResponseModel
    );
    this.vm.getVenueTypes();
    this.vm.getRoomTypes();
    this.vm.getSeatingStyles();

    setTimeout(() => {
      this.handleInputData();
    }, 300);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      console.log('[SnFormVenueV2] @Input data changed from parent:', {
        firstChange: changes['data'].firstChange,
        previousValue: changes['data'].previousValue,
        currentValue: changes['data'].currentValue,
      });

      // Ensure form is initialized before attempting to patch values
      if (this.form) {
        this.handleInputData();
      }
    }

    if (changes['sourcingNeedRecordId']) {
      console.log('[SnFormVenueV2] sourcingNeedRecordId changed from parent:', {
        firstChange: changes['sourcingNeedRecordId'].firstChange,
        previousValue: changes['sourcingNeedRecordId'].previousValue,
        currentValue: changes['sourcingNeedRecordId'].currentValue,
      });
    }
  }

  handleInputData(): void {
    if (this.data) {
      this.existingData = this.data;
      this.isView = false;
      this.isEdit = true;
      this.patchVenueRooms(this.mapApiToVenueRooms(this.data));
      this.form.patchValue({
        is_breakout_rooms_required: this.data.is_breakout_rooms_required ?? false,
        number_of_breakout_rooms: this.data.number_of_breakout_rooms ?? null,
      });
    } else {
      this.existingData = null;
      this.isView = false;
      this.isEdit = true;
      this.form.reset();
      this.patchVenueRooms([{ venue: null, room_type: null, seating_style: null }]);
      this.form.enable();
      this.cdr.detectChanges();
    }
  }

  // Builds the list of { venue, room_type, seating_style } pairs to seed the form from
  // an existing sourcing_venue record.
  mapApiToVenueRooms(data: any): { venue: number | null; room_type: number | null; seating_style: number | null }[] {
    if (Array.isArray(data.venue_rooms_details) && data.venue_rooms_details.length) {
      return data.venue_rooms_details.map((room: any) => ({
        venue: room.venue ?? room.venue_id ?? null,
        room_type: room.room_type ?? room.room_type_id ?? null,
        seating_style: room.seating_style ?? room.seating_style_id ?? null,
      }));
    }

    // Legacy fallback: older records only have a single flat venue/room_type/seating_style
    // (or their *_details arrays) instead of venue_rooms_details.
    const venueId = Array.isArray(data.venue_type_details)
      ? (data.venue_type_details[0]?.id ?? null)
      : (data.venue ?? null);

    const roomType = Array.isArray(data.room_type_details)
      ? (data.room_type_details[0]?.id ?? null)
      : (data.room_type ?? null);

    const seatingStyle = Array.isArray(data.seating_style_details)
      ? (data.seating_style_details[0]?.id ?? null)
      : (data.seating_style ?? null);

    return [{ venue: venueId, room_type: roomType, seating_style: seatingStyle }];
  }

  initForm(): void {
    this.form = this.fb.group({
      venue_rooms: this.fb.array([this.createVenueRoomGroup()]),
      is_breakout_rooms_required: [false, [Validators.required]],
      number_of_breakout_rooms: [null],
      // venue_description: ['',Validators.required],
    });

    // --- Legacy single-select controls (superseded by the venue_rooms FormArray above) ---
    // this.form = this.fb.group({
    //   venue: [null, Validators.required],
    //   room_type: [null, Validators.required],
    //   seating_style: [null, Validators.required],
    //   is_breakout_rooms_required: [false, [Validators.required]],
    //   number_of_breakout_rooms: [null],
    // });
    //
    // this.form.get('venue')?.valueChanges.subscribe((venueId: number | null) => {
    //   const selectedVenueId = this.toNumberOrNull(venueId);
    //   if (this.lastVenueId !== null && selectedVenueId !== this.lastVenueId) {
    //     this.form.patchValue({ room_type: null });
    //   }
    //   this.lastVenueId = selectedVenueId;
    // });
  }

  createVenueRoomGroup(initial?: { venue?: number | null; room_type?: number | null; seating_style?: number | null }): FormGroup {
    const group = this.fb.group({
      venue: [initial?.venue ?? null, Validators.required],
      room_type: [initial?.room_type ?? null, Validators.required],
      seating_style: [initial?.seating_style ?? null, Validators.required],
    });

    let lastVenueId = this.toNumberOrNull(initial?.venue ?? null);
    group.get('venue')?.valueChanges.subscribe((venueId: number | null) => {
      const selectedVenueId = this.toNumberOrNull(venueId);
      if (lastVenueId !== null && selectedVenueId !== lastVenueId) {
        group.patchValue({ room_type: null });
      }
      lastVenueId = selectedVenueId;
    });

    return group;
  }

  get venueRoomsArray(): FormArray {
    return this.form.get('venue_rooms') as FormArray;
  }

  private patchVenueRooms(rooms: { venue: number | null; room_type: number | null; seating_style: number | null }[]): void {
    const array = this.venueRoomsArray;
    while (array.length) {
      array.removeAt(0);
    }
    (rooms.length ? rooms : [{ venue: null, room_type: null, seating_style: null }]).forEach(room => {
      array.push(this.createVenueRoomGroup(room));
    });
  }

  addVenueRoom(): void {
    this.venueRoomsArray.push(this.createVenueRoomGroup());
  }

  removeVenueRoom(index: number): void {
    if (this.venueRoomsArray.length <= 1) {
      return;
    }
    this.venueRoomsArray.removeAt(index);
  }

  getFilteredRoomTypes(rowIndex: number): any[] {
    const selectedVenueId = this.toNumberOrNull(this.venueRoomsArray.at(rowIndex)?.get('venue')?.value);
    const allRoomTypes = this.roomTypesResponseModel.value?.results ?? [];

    if (selectedVenueId === null) {
      return allRoomTypes;
    }

    return allRoomTypes.filter((roomType: any) => {
      const venueTypes = roomType?.venue_type;
      if (!Array.isArray(venueTypes)) {
        return false;
      }

      return venueTypes.some((venueTypeId: number | string) => this.toNumberOrNull(venueTypeId) === selectedVenueId);
    });
  }

  private toNumberOrNull(value: any): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  onSave(): void {
    if (this.form.valid) {
      const payload: any = {
        venue_rooms: this.venueRoomsArray.value,
        is_breakout_rooms_required: this.form.value.is_breakout_rooms_required,
        number_of_breakout_rooms: this.form.value.number_of_breakout_rooms,
      };

      // --- Legacy flat payload (superseded by the venue_rooms array above) ---
      // const payload = {
      //   ...this.form.value,
      //   venue: this.form.value.venue,
      //   room_type: this.form.value.room_type,
      //   seating_style: this.form.value.seating_style,
      // };

      payload.sourcing_need = this.sourcingNeedRecordId;
      if (payload.number_of_breakout_rooms == null || payload.number_of_breakout_rooms == 0) {
        delete payload.number_of_breakout_rooms;
      }
      payload.is_venue_required = true;

      const loggedInUser = AppCommon.getloggedInObject();
      const userId = loggedInUser?.user?.id;

      if (this.isEdit && this.existingData) {
        payload.id = this.existingData.id;
        payload.modified_by = userId;
        this.vm.addUpdateSouringVenue(payload, true);
      } else {
        payload.created_by = userId;
        console.log('Adding new venue with payload:', payload);
        // return;
        this.vm.addUpdateSouringVenue(payload, false);
      }
    }
  }

  sourcingVenueAdded() {
    this.closeModal.emit('success');

    if (this.eventId) {
      this.vm.getSouringNeedbyId(this.eventId, 'cet-sourcing-needs-v2');
    }
  }

  onEdit(): void {
    this.isEdit = true;
    this.isView = false;
    this.form.enable();
  }

  onCancel(): void {
    this.closeModal.emit('cancel');
  }
}
